import { useMemo, type ReactNode } from 'react';

import { AdminContextMenu } from '@rocco/ui/tile-shared/components/AdminContextMenu';
import type { DirectoryItemAdmin } from '~/modules/directory/type';
import { mapDirectoryItemAdminToTile } from '../data-mapper/directory-item-admin-to-tile';
import { useTileAdminActions } from '../hooks/use-tile-admin-actions';

interface DetailTileAdminMenuProps {
    /**
     * Admin metadata for this item (from `useItemAdminData`). Absent for users
     * without edit permission — the tile then renders plain.
     */
    adminData?: DirectoryItemAdmin;
    itemId: number;
    itemLink: string;
    /** The owning professional's profile link (used by the edit action). */
    professionalLink: string;
    itemType: 'projects' | 'products';
    children: ReactNode;
}

/**
 * Inner menu — only mounted when admin data is present, so the admin-action
 * hook (mutations/fetchers) never runs for regular visitors.
 */
const AdminMenu = ({
    adminData,
    itemId,
    itemLink,
    professionalLink,
    itemType,
    children,
}: DetailTileAdminMenuProps & { adminData: DirectoryItemAdmin }) => {
    const statusInfo = useMemo(
        () => mapDirectoryItemAdminToTile(adminData),
        [adminData],
    );
    const actions = useTileAdminActions({
        item: {
            id: String(itemId),
            link: itemLink,
            professionalLink,
            pinned: statusInfo.pinned,
            featuredBrandPinned: statusInfo.featuredBrandPinned,
            productsAllowed: statusInfo.productsAllowed,
        },
        itemType,
    });

    return (
        <AdminContextMenu statusInfo={statusInfo} {...actions}>
            {children}
        </AdminContextMenu>
    );
};

/**
 * Wraps a revamp detail tile with the BM admin context menu (edit, reposition
 * image, pin, delete + KPI/status), matching the per-tile admin controls the
 * legacy directory/overview tiles carry. Non-admins (no `adminData`) get the
 * tile unchanged.
 */
export const DetailTileAdminMenu = ({
    adminData,
    children,
    ...rest
}: DetailTileAdminMenuProps) => {
    if (!adminData) return <>{children}</>;
    return (
        <AdminMenu adminData={adminData} {...rest}>
            {children}
        </AdminMenu>
    );
};
